import { useMemo, useReducer } from 'react';
import { initial } from './loggerDraftHelpers';
import type { LoggerAction } from './loggerDraftTypes';
import { loggerReducer } from './loggerNestedReducer';
import { assembleDraftKeepIds, flattenDraft } from './loggerTimeline';
import type { WorkoutState } from './loggerTimelineTypes';
import { uuidv4 } from '../uuid';

// Public store API for the Logger. The state shape + action contract live in
// ./loggerDraftTypes, the pure state helpers in ./loggerDraftHelpers, and the
// nested reducer in ./loggerNestedReducer. This module owns the live flat
// store (WorkoutState) + the useLoggerDraft hook, and re-exports the public
// surface so the existing '../state/loggerDraft' import path stays stable for
// every caller (consumers and the reducer tests).
export * from './loggerDraftTypes';
export { initial };
export { loggerReducer };

// The live source of truth is the flat WorkoutState (timeline[] + exercises
// roster). Each dispatch derives the nested view WITH identity fields
// (assembleDraftKeepIds), runs the existing, fully-tested nested reducer, and
// re-flattens — flattenDraft reuses the ids the reducer carried through its
// spreads, so a logged set keeps its id across edits while new sets get fresh
// ones. Because loggerReducer never reads the id fields,
//   assembleDraft(flatReducer(s, a)) === loggerReducer(assembleDraft(s), a),
// i.e. behaviour is byte-identical to the old nested hook (proven in
// __tests__/loggerTimelineReducer.test.ts). This reuses all ~30 actions
// instead of re-implementing them against the flat shape.
export function flatReducer(
  state: WorkoutState,
  action: LoggerAction,
): WorkoutState {
  return flattenDraft(loggerReducer(assembleDraftKeepIds(state), action), uuidv4);
}

const initialWorkoutState = (): WorkoutState => flattenDraft(initial(), uuidv4);

export function useLoggerDraft() {
  const [state, dispatch] = useReducer(
    flatReducer,
    undefined,
    initialWorkoutState,
  );
  // Consumers (the timeline logger, totals, history, save) read the nested
  // LoggerDraft exactly as before; the flat store is internal. The raw
  // WorkoutState is exposed as a 3rd element ONLY for persistence, which
  // serializes its id-carrying projection so set ids survive a cold boot
  // (see useDraftPersistence). Existing `const [draft, dispatch] = ...`
  // callsites ignore it.
  //
  // LG-39: this is assembleDraftKeepIds, NOT assembleDraft. The identity was
  // always in the flat store (ExerciseMeta.entryId) and the stripping variant
  // dropped it one step before the UI could see it — so every "which row am I
  // on" question fell back to the catalog slug, which is not unique when the
  // same exercise appears twice in a workout. Keeping the ids is the whole
  // fix; nothing else needed minting. buildPayload writes the API entry with
  // an explicit allow-list (workoutPayload.ts), so the extra field cannot
  // reach the server.
  const draft = useMemo(() => assembleDraftKeepIds(state), [state]);
  return [draft, dispatch, state] as const;
}

export type LoggerDispatch = ReturnType<typeof useLoggerDraft>[1];
